// Omnix/client/ui/AgentManagerCrud.js
import { ComponentBase } from "../core/ComponentBase.js";

export class AgentManagerCrud extends ComponentBase {
    constructor(config) {
        super(config);
        this.dataCrud = null;
        this.currentContextId = null;
        this._boundHandleContextChange = this.handleContextChange.bind(this);
        this.agentConfigRoute = this.config.agentConfigRoute || '/admin/agent-config';
    }

    async initialize() {
        if (this.config.contextChangeEventName) {
            this.appClient.events.local.on(this.config.contextChangeEventName, this._boundHandleContextChange);
        } else {
            this.appClient.events.local.on('projectSelected', this._boundHandleContextChange);
        }
        await this.initializeDataCrud();
        await super.initialize();
    }

    async initializeDataCrud() {
        if (this.dataCrud) {
            this.dataCrud.destroy();
            this.dataCrud = null;
        }

        const dataProviderContext = {};
        if (this.currentContextId) {
            // --- THIS IS THE FIX ---
            // Use the generic 'contextId' key which the backend service will now expect.
            dataProviderContext.contextId = this.currentContextId;
            // --- END OF FIX ---
        }

        this.dataCrud = await this.createManagedComponent("DataCrud", null, {
            instanceId: `${this.config.instanceId}-crud`,
            rpcClass: "AgentConfigRpc",
            schemaPath: "collections/agentConfigs",
            title: "AI Agent Management",
            viewConfig: {
                mode: 'grid',
                disableDefaultCreate: true,   // 'Create Agent' navigates to config view
                disableDefaultEdit:   true,   // 'Configure' navigates to config view
                disableDefaultView:   true,   // no inline form on row click
                disableEdit:          true,   // suppress auto Edit button
                fields: [
                    { 
                        name: 'name', 
                        label: 'Agent Name',
                        render: (val, item) => `${val} ${item.isSystemAgent ? '<span class="badge bg-info text-dark ms-2">System</span>' : ''}`
                    },
                    { name: 'description', label: 'Description' },
                    { name: 'model', label: 'Model' },
                ],
                actions: [
                    { 
                        label: 'Configure', 
                        icon: 'bi-gear-fill', 
                        eventName: 'configureAgent',
                        visibleWhen: (item) => !item.isSystemAgent // Only show if it's NOT a system agent
                    },
                    {
                        label: 'Delete',
                        icon: 'bi-trash',
                        eventName: 'deleteItem', // This is the default event name DataCrud uses
                        className: 'btn-outline-danger',
                        visibleWhen: (item) => !item.isSystemAgent // Only show if it's NOT a system agent
                    }
                ],
                toolbar: { create: { enabled: true, label: "Create Agent" } }
            },

            formConfig: {
                fields: {
                    name: { type: 'text', label: 'Agent Name' }
                }
            },
            dataConfig: {
                autoLoad: false,
                context: dataProviderContext
            }
        });

        this.dataCrud.dataView.on(`configureAgent`, (item) => {
            if (item && item._id) {
                this.appClient.layoutEngine.navigate(`${this.agentConfigRoute}?agentId=${item._id}`);
            }
        });

        this.dataCrud.dataView.on(`createItem`, () => {
            this.appClient.layoutEngine.navigate(`${this.agentConfigRoute}`);
        });
    }

    async handleContextChange(eventData) {
        const newContextId = eventData?.projectData?._id || eventData?._id || null;
        if (this.currentContextId === newContextId) return;
        
        this.currentContextId = newContextId;
        this.log.info(`Context changed in AgentManagerCrud: ${this.currentContextId}`);

        if (this.dataCrud) {
            // --- THIS IS THE FIX ---
            // Pass the generic 'contextId' to the DataCrud's data provider.
            const newContext = this.currentContextId ? { contextId: this.currentContextId } : {};
            // --- END OF FIX ---
            this.dataCrud.setContext(newContext);
            await this.dataCrud.refreshData();
        } else {
            await this.initializeDataCrud();
        }
        if (this.container) await this.render();
    }

    async initAfterLogin() {
        let initialContextData = null;
        if (!this.config.contextChangeEventName) {
            initialContextData = this.appClient.getSharedData('selectedProject');
        }
        
        if (initialContextData) {
            this.currentContextId = initialContextData._id;
            await this.dataCrud.refreshData();
        } else if (!this.config.contextChangeEventName) {
            this.currentContextId = null;
            await this.dataCrud.refreshData();
        } else {
            this.currentContextId = null;
        }
    }

    async render() {
        if (!this.container) {
            this.container = document.createElement('div');
        }
        this.container.innerHTML = '';

        if (this.config.contextChangeEventName && !this.currentContextId) {
            const placeholder = document.createElement('div');
            placeholder.className = 'alert alert-info text-center';
            placeholder.innerHTML = `<i class="bi bi-info-circle-fill me-2"></i> Please select a context to manage AI agents.`;
            this.container.appendChild(placeholder);
        } else if (this.dataCrud) {
            this.container.appendChild(await this.dataCrud.render());
        } else {
            this.container.textContent = 'Loading agent manager...';
        }
        return this.container;
    }

    destroy() {
        if (this.config.contextChangeEventName) {
            this.appClient.events.local.off(this.config.contextChangeEventName, this._boundHandleContextChange);
        } else {
            this.appClient.events.local.off('projectSelected', this._boundHandleContextChange);
        }
        this.dataCrud?.destroy();
        super.destroy();
    }
}